// Rechner: Bilder gleichmäßig aufhängen. Interaktive Drag-Vorschau, live.
import React, { useEffect, useMemo, useState } from "react";
import { calcEvenPictures, fmtCm } from "@/lib/calc";
import { track, trackSettled } from "@/lib/track";
import EditorShell from "./EditorShell";
import { startDrag, round1, clamp, fmt, niceStep, niceCeil } from "@/components/preview/useDragSvg";
import { C, Svg, handle, cross, pill, hDim } from "@/components/preview/canvasPrimitives";

const DEFAULTS = {
  wallWidth: "200",
  count: "3",
  pictureWidth: "40",
  pictureHeight: "50",
  centerFromFloor: "150",
  hangerFromTop: "8",
};

const W = 880;
const H = 470;
const padL = 64;
const padR = 836;
const wallTopY = 24;
const floorY = 408;

export default function EvenPicturesCalculator() {
  const [v, setV] = useState(DEFAULTS);
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const setNow = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const num = (k) => {
    const x = parseFloat(String(v[k]).replace(",", "."));
    return isNaN(x) ? NaN : x;
  };

  const r = useMemo(() => calcEvenPictures(v), [v]);

  useEffect(() => {
    if (!r.ok) return;
    trackSettled("calculator_used", { calculator: "even_pictures", mode: "default" });
    trackSettled("result_shown", { calculator: "even_pictures", verdict: (r.warnings && r.warnings.length) ? "warn" : "ok" });
  }, [r]);

  const canvas = renderCanvas(v, r, num, setNow);
  const copyText = r.ok ? buildCopyText(r) : "";

  const fields = [
    { id: "wallWidth", label: "Wandbreite", value: v.wallWidth, step: "1", onChange: set("wallWidth") },
    { id: "pictureWidth", label: "Bildbreite", value: v.pictureWidth, step: "0.5", onChange: set("pictureWidth") },
    { id: "pictureHeight", label: "Bildhöhe", value: v.pictureHeight, step: "0.5", onChange: set("pictureHeight") },
    { id: "centerFromFloor", label: "Bildmitte ab Boden", value: v.centerFromFloor, step: "1", onChange: set("centerFromFloor") },
    {
      id: "hangerFromTop",
      label: "Aufhängung von Oberkante",
      value: v.hangerFromTop,
      step: "0.5",
      onChange: set("hangerFromTop"),
      hint: "Abstand der Öse oder Schnur zur Bildoberkante.",
    },
  ];

  return (
    <EditorShell
      testId="calc-even-pictures"
      canvas={canvas}
      canvasReady={r.ok}
      emptyHint="Gib Wandbreite, Anzahl und Bildbreite ein, um die Vorschau zu sehen."
      toolbarHint="Ziehe die Anfasser an Wand und Bild, oder justiere die Maße unten."
      count={{
        value: r.ok ? r.count : Math.max(1, Math.round(num("count")) || 1),
        label: "Bilder",
        onInc: () => setNow("count", String(Math.min(8, (Math.round(num("count")) || 1) + 1))),
        onDec: () => setNow("count", String(Math.max(1, (Math.round(num("count")) || 1) - 1))),
      }}
      onReset={() => setV(DEFAULTS)}
      fields={fields}
      warnings={r.ok ? r.warnings : []}
      hasResult={r.ok}
      results={r.ok ? buildResults(r) : []}
      steps={r.ok ? buildSteps(r) : []}
      copyText={copyText}
    />
  );
}

function renderCanvas(v, r, num, setNow) {
  const wallWidth = num("wallWidth") || 1;
  const drawW = padR - padL;
  const scaleX = drawW / wallWidth;
  const sx = (cm) => padL + cm * scaleX;
  const xToCm = (px) => (px - padL) / scaleX;
  const ph = num("pictureHeight") || 0;
  const cf = num("centerFromFloor") || 0;
  const topEdge = cf + ph / 2;
  const viewCm = niceCeil(Math.max(topEdge + 22, 180));
  const scaleY = (floorY - wallTopY) / viewCm;
  const vy = (cm) => floorY - cm * scaleY;
  const yToCm = (px) => (floorY - px) / scaleY;
  const startPW = num("pictureWidth");
  const startCF = num("centerFromFloor");
  const els = [];
  const overflow = r.ok && r.totalWidth > wallWidth;

  els.push(
    <rect
      key="wall"
      x={padL}
      y={wallTopY}
      width={drawW}
      height={floorY - wallTopY}
      fill={C.wall}
      stroke={overflow ? C.warn : C.wallStroke}
      strokeWidth={1.5}
      rx={4}
    />
  );

  const stepV = niceStep(viewCm);
  for (let h = 0; h <= viewCm + 0.1; h += stepV) {
    const y = vy(h);
    els.push(<line key={"lt" + h} x1={padL - 6} y1={y} x2={padL} y2={y} stroke={C.wallStroke} strokeWidth={1} />);
    els.push(
      <text key={"ll" + h} x={padL - 9} y={y + 4} fill={C.dim} fontSize={11} textAnchor="end" fontFamily="Inter,sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>
        {h}
      </text>
    );
  }
  const stepH = niceStep(wallWidth);
  for (let x = 0; x <= wallWidth + 0.1; x += stepH) {
    const px = sx(x);
    els.push(<line key={"bt" + x} x1={px} y1={floorY} x2={px} y2={floorY + 6} stroke={C.wallStroke} strokeWidth={1} />);
    els.push(
      <text key={"bl" + x} x={px} y={floorY + 20} fill={C.dim} fontSize={11} textAnchor="middle" fontFamily="Inter,sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>
        {fmt(x)}
      </text>
    );
  }
  els.push(<line key="floor" x1={padL} y1={floorY} x2={padR} y2={floorY} stroke={C.floor} strokeWidth={2.5} />);

  const wcx = sx(wallWidth / 2);
  els.push(<line key="wc" x1={wcx} y1={wallTopY - 4} x2={wcx} y2={floorY} stroke={C.center} strokeWidth={1.4} strokeDasharray="6 5" />);
  els.push(pill("wcl", wcx, wallTopY + 8, "Wandmitte", C.center, "middle", W));

  if (r.ok && r.pictures.length) {
    const pw = r.pictureWidth * scaleX;
    const yTop = ph > 0 ? vy(topEdge) : wallTopY + 60;
    const pxH = ph > 0 ? ph * scaleY : 120;
    const drillY = r.drillHeight != null ? vy(r.drillHeight) : yTop + 24;
    const moveBody = (ev) =>
      startDrag(ev, W, H, (cur, st) => {
        setNow("centerFromFloor", String(round1(clamp(startCF - (cur.y - st.y) / scaleY, ph / 2 || 5, viewCm))));
      });
    r.pictures.forEach((p) => {
      const x = sx(p.left);
      els.push(
        <rect
          key={"pic" + p.index}
          x={x}
          y={yTop}
          width={Math.max(3, pw)}
          height={pxH}
          fill={C.object}
          stroke={C.objectStroke}
          strokeWidth={2}
          rx={3}
          style={{ cursor: "ns-resize" }}
          onPointerDown={moveBody}
        />
      );
      els.push(
        <text key={"plb" + p.index} x={x + pw / 2} y={yTop + pxH / 2 + 5} fill={C.objectStroke} fontSize={15} fontWeight={600} textAnchor="middle" fontFamily="Inter,sans-serif" style={{ pointerEvents: "none" }}>
          {p.index}
        </text>
      );
      els.push(<line key={"dg" + p.index} x1={sx(p.drillX)} y1={drillY} x2={sx(p.drillX)} y2={floorY} stroke={C.guide} strokeWidth={1} strokeDasharray="3 4" />);
    });
    const dimY = Math.min(floorY - 14, yTop + pxH + 18);
    if (r.pictures.length >= 2 && r.gap >= 0) {
      els.push(hDim("gap", sx(r.pictures[0].right), sx(r.pictures[1].left), dimY, fmt(r.gap) + " cm", C.dim, W));
    }
    if (r.leftMargin >= 0) {
      els.push(hDim("lm", padL, sx(r.pictures[0].left), dimY, fmt(r.leftMargin), C.dim, W));
    }
    r.pictures.forEach((p, i) => {
      const dx = sx(p.drillX);
      if (i === 0) {
        els.push(
          cross("cr" + i, dx, drillY, (ev) =>
            startDrag(ev, W, H, (cur) => {
              const hanger = clamp(topEdge - yToCm(cur.y), 0, ph || 200);
              setNow("hangerFromTop", String(round1(hanger)));
            })
          )
        );
      } else {
        els.push(cross("cr" + i, dx, drillY, null));
      }
    });
    if (r.drillHeight != null) {
      els.push(pill("dh", sx(wallWidth) - 8, drillY, "Bohrhöhe " + fmt(r.drillHeight) + " cm", C.primary, "end", W));
    }
    const midY = (yTop + Math.min(floorY, yTop + pxH)) / 2;
    els.push(
      handle("hw", sx(wallWidth), (wallTopY + floorY) / 2, "ew-resize", (ev) =>
        startDrag(ev, W, H, (cur) => setNow("wallWidth", String(round1(clamp(xToCm(cur.x), 40, 600))))), "v")
    );
    els.push(
      handle("hpw", sx(r.pictures[0].right), midY, "ew-resize", (ev) =>
        startDrag(ev, W, H, (cur, st) => setNow("pictureWidth", String(round1(clamp(startPW + (cur.x - st.x) / scaleX, 5, 200))))), "v")
    );
    if (ph > 0) {
      els.push(
        handle("hph", sx(r.pictures[0].left) + 15, yTop, "ns-resize", (ev) =>
          startDrag(ev, W, H, (cur) => setNow("pictureHeight", String(round1(clamp(2 * (yToCm(cur.y) - cf), 5, 200))))), "h")
      );
    }
  }
  if (overflow) {
    els.push(pill("ov", W / 2, wallTopY + 34, "Passt nicht, Bilder breiter als Wand", C.warn, "middle", W));
  }

  return (
    <Svg vbW={W} vbH={H} ariaLabel="Vorschau: Bilder gleichmäßig aufhängen">
      {els}
    </Svg>
  );
}

function buildResults(r) {
  const rows = [
    { label: "Abstand zwischen den Bildern", value: fmtCm(r.gap) },
    { label: "Abstand zur linken Wandkante", value: fmtCm(r.leftMargin) },
    { label: "Abstand zur rechten Wandkante", value: fmtCm(r.rightMargin) },
    ...r.pictures.map((p) => ({ label: `Bohrpunkt Bild ${p.index} (von links)`, value: fmtCm(p.drillX) })),
  ];
  if (r.drillHeight !== null) rows.push({ label: "Bohrhöhe ab Boden", value: fmtCm(r.drillHeight) });
  return rows;
}

function buildSteps(r) {
  const steps = [
    `Markiere die Bohrpunkte von der linken Wandkante aus: ${r.pictures.map((p) => fmtCm(p.drillX)).join(", ")}.`,
  ];
  steps.push(
    r.drillHeight !== null
      ? `Markiere die Bohrhöhe bei ${fmtCm(r.drillHeight)} ab dem Boden.`
      : "Lege die Bohrhöhe fest (Bildmitte ab Boden und Bildhöhe eingeben)."
  );
  steps.push("Prüfe alle Markierungen mit einer Wasserwaage, bevor du bohrst.");
  steps.push("Achte vor dem Bohren auf Leitungen und die Wandbeschaffenheit.");
  return steps;
}

function buildCopyText(r) {
  const lines = [
    "Bilder gleichmäßig aufhängen",
    `Wand ${fmtCm(r.wallWidth)}, ${r.count} Bilder à ${fmtCm(r.pictureWidth)} Breite`,
    `Abstand zwischen den Bildern: ${fmtCm(r.gap)}`,
    `Abstand zur linken Wandkante: ${fmtCm(r.leftMargin)}`,
    `Abstand zur rechten Wandkante: ${fmtCm(r.rightMargin)}`,
    ...r.pictures.map((p) => `Bohrpunkt Bild ${p.index} (von links): ${fmtCm(p.drillX)}`),
  ];
  if (r.drillHeight !== null) lines.push(`Bohrhöhe ab Boden: ${fmtCm(r.drillHeight)}`);
  return lines.join("\n");
}
